// === Shopify EUR→BGN Fixed-Rate Currency Converter v1.3 ===
(function (window, document) {
  'use strict';

  if (window.BgnEurLiveConverter) return;

  const DEFAULTS = {
    fixedRate: 1.95583, // 1 EUR = 1.95583 BGN
    selectors: [
      '.price',
      '.money',
      '[class*="price"]',
      '.amount',
      '[class*="amount"]',
      '.cart__price',
      'ins',
      '.cart__price span',
    ],
    batchTime: 50,
    idleDelay: 200,
    debug: false,
    localeBG: 'bg-BG',
    localeEU: 'en-US',
    decimals: 2,
  };

  let cfg, rateEURtoBGN, observer, enabled;
  const stats = {converted: 0, skipped: 0, errors: 0, lastBatch: 0};
  const seen = new WeakSet();

  function log(...args) {
    cfg.debug && console.debug('[EUR→BGN]', ...args);
  }

  function initConfig(userCfg = {}) {
    cfg = {...DEFAULTS, ...userCfg};
  }

  // Set fixed rate synchronously
  function setFixedRate() {
    rateEURtoBGN = cfg.fixedRate;
    log('Fixed rate set: 1 EUR =', rateEURtoBGN, 'BGN');
  }

  function extractNumber(str) {
    const cleaned = str.replace(/[^\d,.-]/g, '').trim();

    // European format: 1.390 or 1.390,00 (dot is thousand separator)
    // Check if it ends with 3 digits after a dot (thousand separator, not decimal)
    if (/^\d{1,3}\.\d{3}$/.test(cleaned)) {
      // e.g., "1.390" -> 1390
      return parseFloat(cleaned.replace(/\./g, ''));
    }

    // European format with comma decimal: 1.390,00
    if (cleaned.includes(',') && cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      return parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    }

    // US format or simple decimal: 1,390.00 or 1.39
    return parseFloat(cleaned.replace(/,/g, ''));
  }

  function format(num, locale) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: cfg.decimals,
      maximumFractionDigits: cfg.decimals,
    }).format(num);
  }

  // Only match EUR amounts (€ or EUR)
  const moneyRE = new RegExp(
    ['(?<!\\()', '([0-9]{1,3}(?:[.,][0-9]{3})*(?:[.,][0-9]{1,2})?)', '\\s*', '([€]|EUR)', '(?![^()]*\\))'].join(''),
    'gi'
  );

  function alreadyConverted(text) {
    return /\([^)]*(?:лв|BGN)[^)]*\)/.test(text) || /лв|BGN/i.test(text);
  }

  function replacer(match, amt, sym) {
    const n = extractNumber(match);
    if (isNaN(n) || n <= 0) {
      stats.skipped++;
      return match;
    }
    // Convert EUR to BGN
    const converted = n * rateEURtoBGN;
    const formatted = format(converted, cfg.localeBG);
    stats.converted++;
    // wrap the entire output in a .converted span
    return `<span class="converted">${match} <span class="conversion-pipe">|</span> ${formatted} лв</span>`;
  }

  function processBatch(nodes) {
    const start = performance.now();
    let processed = 0;

    while (nodes.length && performance.now() - start < cfg.batchTime) {
      const node = nodes.shift();
      if (seen.has(node)) continue;

      const parentEl = node.parentNode;
      if (parentEl && parentEl.closest('.old-price, .line-through, .product__price--strike')) {
        stats.skipped++;
        seen.add(node);
        continue;
      }

      const txt = node.textContent;
      if (txt && !alreadyConverted(txt) && moneyRE.test(txt)) {
        try {
          // Build HTML string with our <span> wrapper
          const html = txt.replace(moneyRE, replacer);

          // Create a DocumentFragment from the HTML
          const range = document.createRange();
          range.selectNodeContents(node);
          const frag = range.createContextualFragment(html);

          // Replace the text node with the HTML fragment
          node.parentNode.replaceChild(frag, node);

          seen.add(node);
          processed++;
        } catch (e) {
          stats.errors++;
          log('Conversion error:', e);
        }
      }
    }

    stats.lastBatch = performance.now() - start;
    return processed;
  }

  function collectTextNodes() {
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          // skip empty or struck-through text
          if (!node.nodeValue.trim() || node.parentElement.tagName.toLowerCase() === 'del') {
            return NodeFilter.FILTER_REJECT;
          }
          // skip anything inside our conversion wrapper or pipe
          if (node.parentElement.closest('.converted, .conversion-pipe')) {
            return NodeFilter.FILTER_REJECT;
          }
          // only accept text under one of our selectors
          return node.parentNode.closest(cfg.selectors.join(',')) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        },
      },
      false
    );

    const out = [];
    let n;
    while ((n = walker.nextNode())) out.push(n);
    return out;
  }

  function scanAndConvert() {
    if (!enabled) return;
    const nodes = collectTextNodes();
    if (!nodes.length) return;
    (function run() {
      if (!nodes.length) return;
      processBatch(nodes);
      if (nodes.length) {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, {timeout: cfg.batchTime});
        } else {
          setTimeout(run, 0);
        }
      }
    })();
  }

  function startObserver() {
    if (!('MutationObserver' in window)) return;
    observer = new MutationObserver(() => {
      clearTimeout(observer._timer);
      observer._timer = setTimeout(scanAndConvert, cfg.idleDelay);
    });
    observer.observe(document.body, {childList: true, subtree: true});
  }

  const api = {
    init(options) {
      initConfig(options);
      enabled = true;
      setFixedRate();
      scanAndConvert();
      startObserver();
      log('Converter initialized (fixed rate).');
    },
    pause() {
      enabled = false;
    },
    resume() {
      enabled = true;
      scanAndConvert();
    },
    updateRate(newRate) {
      // still allow manual override if ever needed
      cfg.fixedRate = newRate;
      setFixedRate();
    },
    resetStats() {
      Object.assign(stats, {converted: 0, skipped: 0, errors: 0, lastBatch: 0});
    },
    getStats() {
      return {...stats};
    },
    disable() {
      this.pause();
      if (observer) observer.disconnect();
      log('Converter disabled.');
    },
  };

  window.BgnEurLiveConverter = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.init(), {once: true});
  } else {
    api.init();
  }
})(window, document);
